import { useEffect, useState } from "react"
import { useAppSelector } from "../../../../../../hooks/store-hooks"
import { ILocalizacao } from "../../../../../../../application/models"
import { useLocalizacaoService, useRecortesExtracaoService } from "../../../../../../../use-cases/geo-veles"
import { Autocomplete, debounce, Grid, TextField } from "@mui/material"
import { categoriaLocalizacaoOptions } from "../../../detalhes-do-projeto/filtros/localizacao/LocalizacaoDialog"
// Icon
import { RecorteListasCidadeEdit } from "../listas-cidade"
import { RecorteListaCEP } from "../lista-cep"

export const RecorteLocalizacao = () => {
    // State
    const { localizacoes, searchTerm, categoria } = useAppSelector((state) => state.localizacao)
    const { openForm, editRecorteExtracaoForm } = useAppSelector((state) => state.recorteExtracao)

    // Use State - Agora mantém seleções separadas por categoria
    const [selectedLocalizacaoPorCategoria, setSelectedLocalizacaoPorCategoria] = useState<Record<string, ILocalizacao[]>>({})
    const [selectedCategoria, setSelectedCategoria] = useState<string>('')

    // Hook
    const {
        setCategoria,
        setSearchTerm,
        searchLocalizacao,
        resetLocalizacaoSlice,
        resetLocalizacaoSearchTerm,
        resetLocalizacoesList
    } = useLocalizacaoService()
    const { syncFilterWithForm, removeFilterFromForm } = useRecortesExtracaoService()

    useEffect(() => {
        const localizacoesDoRedux = editRecorteExtracaoForm.fields.Filtros?.localizacao || [];

        if (localizacoesDoRedux.length > 0) {
            // Agrupa as localizações do Redux por categoria
            const grouped = localizacoesDoRedux.reduce((acc, loc) => {
                const cat = loc.categoria || 'default';
                if (!acc[cat]) {
                    acc[cat] = [];
                }
                acc[cat].push(loc);
                return acc;
            }, {} as Record<string, ILocalizacao[]>);

            setSelectedLocalizacaoPorCategoria(grouped);
        } else {
            setSelectedLocalizacaoPorCategoria({});
        }
    }, [editRecorteExtracaoForm.fields.Filtros?.localizacao]);

    // Obtém as localizações da categoria atual
    const currentCategorySelections = selectedLocalizacaoPorCategoria[selectedCategoria] || [];

    // Filtra as opções para remover os itens já selecionados NA CATEGORIA ATUAL
    const filteredLocalizacoes = localizacoes.filter((localizacao: ILocalizacao) =>
        !currentCategorySelections.some((selected: ILocalizacao) =>
            selected.value === localizacao.value && selected.categoria === localizacao.categoria
        )
    );

    // Functions
    const debouncedSetSearchTerm = debounce((newSearchTerm: string) => {
        setSearchTerm(newSearchTerm)
    }, 500)


    const handleLocalizacaoSelectionChange = (value: ILocalizacao[]) => {
        // Atualiza apenas a categoria atual
        const updated = {
            ...selectedLocalizacaoPorCategoria,
            [selectedCategoria]: value
        };

        setSelectedLocalizacaoPorCategoria(updated);

        // Junta todas as localizações de todas as categorias
        const todasLocalizacoes = Object.values(updated).flat();

        if (todasLocalizacoes.length > 0) {
            syncFilterWithForm({
                filtro: 'localizacao',
                value: todasLocalizacoes,
                formType: 'edit'
            });
        } else {
            removeFilterFromForm({
                filtro: 'localizacao',
                formType: 'edit'
            });
        }
    }

    const handleCategoriaChange = (newValue: { label: string, value: string } | null) => {
        if (newValue === null) {
            setSelectedCategoria('')
            setCategoria('' as keyof ILocalizacao)
            return
        }

        setSelectedCategoria(newValue.value)
        setCategoria(newValue.value as keyof ILocalizacao)
    }

    // Use Effect
    useEffect(() => {
        if (searchTerm.length) {
            searchLocalizacao()
        }
    }, [searchLocalizacao, searchTerm])

    useEffect(() => {
        resetLocalizacaoSearchTerm()
        resetLocalizacoesList()
    }, [categoria, resetLocalizacaoSearchTerm, resetLocalizacoesList])

    useEffect(() => {
        // Limpar apenas quando o formulário for fechado/reaberto
        resetLocalizacaoSlice()
        setSelectedLocalizacaoPorCategoria({})
        setSelectedCategoria('')
    }, [openForm, resetLocalizacaoSlice])

    const renderComponentByCategoria = () => {
        switch (selectedCategoria) {
            case 'cep':
                return <RecorteListaCEP />
            case 'listasCidade':
                return <RecorteListasCidadeEdit />
            default:
                return (
                    <>
                        <Grid item xs={8} my={2} px={2}>
                            <Autocomplete
                                multiple
                                id="tags-standard"
                                options={filteredLocalizacoes}
                                getOptionLabel={(option) => {
                                    if (option.value) {
                                        if (option.categoria && option.categoria === 'bairro') {
                                            return `${option.bairro} - ${option.cidade} / ${option.uf}`
                                        } else {
                                            return option.value ?? ''
                                        }
                                    }
                                    return ''
                                }}
                                sx={{
                                    px: 2
                                }}
                                value={currentCategorySelections}
                                onInputChange={(event, newInputValue) => {
                                    debouncedSetSearchTerm(newInputValue)
                                }}
                                onChange={(event, value) => {
                                    handleLocalizacaoSelectionChange(value);
                                }}
                                noOptionsText={searchTerm.length > 1 ? 'Nenhuma localização encontrada' : 'Digite a localização'}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        variant="outlined"
                                        label="Opções"
                                    />
                                )}
                            />
                        </Grid>
                    </>
                )
        }
    }

    return (
        <>
            <Grid item xs={3} my={2} px={2}>
                <Autocomplete
                    options={categoriaLocalizacaoOptions}
                    value={categoriaLocalizacaoOptions.find(opt => opt.value === selectedCategoria) || null}
                    onChange={(event: any, newValue: { label: string, value: string } | null) => {
                        handleCategoriaChange(newValue)
                    }}
                    renderInput={(props) =>
                        <TextField
                            {...props}
                            label="Selecione o tipo de localização" />
                    }
                />
            </Grid>
            {renderComponentByCategoria()}
        </>
    )
}